import React from "react";
import {useFormik} from "formik";
import {useTranslation} from "react-i18next";
import {parseISO} from "date-fns";

import InfoSection from "./InfoSection";
import SnowMeteorologicalData, {snowMeteorologicalDataColumns} from "./SnowMeteorologicalData";
import SnowBaseData, {snowBaseDataColumns} from "./SnowBaseData";
import ObservedAvalanches, {observedAvalanchesColumns} from "./ObservedAvalanches";
import AvalancheDanger, {avalancheDangerColumns} from "./AvalancheDanger";
import SnowData, {snowDataColumns} from "./SnowData";
import Notes from "./Notes";
import {useDeleteModel1, useModel1RegistryQuery, useModel1ValidationRegistryQuery, useModel1FailedValidationsMutation} from "../query";
import useScroll from "../../../components/useScroll";
import MDLoadingButton from "../../../components/MDLoadingButton";
import useValidations from "./useValidations";
import MDDeleteButton from "../../../components/MDDeleteButton";
import useModel1Authorization from "./useModel1Authorization";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import MDButton from "materialDashboard/components/MDButton";
import MDBox from "materialDashboard/components/MDBox";
import MDTypography from "materialDashboard/components/MDTypography";
import AuditInfo from "../../../components/AuditInfo";
import {useCurrentUser} from "../../../auth/CurrentUserProvider";
import {useModel1Schema} from "../../../schemaValidations/model1schema";
import {useFeedbackTools} from "../../../components/FeedbackTools";
import LoadingOverlay from "../../../components/LoadingOverlay";


const scrollMarginTop = "116px";

export const formikPropertyEqual = (prevProps, nextProps, fields) => {
    return fields.every(property => (
        prevProps.formik.values[property] === nextProps.formik.values[property]
        && prevProps.formik.errors[property] === nextProps.formik.errors[property]
        && prevProps.formik.touched[property] === nextProps.formik.touched[property]
    ));
}

const Model1Form = ({title, initialData = {}, onSubmit}) => {
    const model1Registry = useModel1RegistryQuery();
    const model1ValidationRegistryQuery = useModel1ValidationRegistryQuery();
    const model1FailedValidationsMutation = useModel1FailedValidationsMutation();
    const {t} = useTranslation(['models1', 'validation']);
    const {askConfirmation} = useFeedbackTools();

    const {currentUser} = useCurrentUser();
    const visibleColumns = currentUser.data.userSettings?.model1ColumnsVisibility

    const deleteModel1 = useDeleteModel1();

    const authorization = useModel1Authorization(initialData);
    const inputDisabled = !authorization.edit;

    const [infoSectionRef, scrollToInfoSection] = useScroll();
    const [snowMeteorologicalDataRef, scrollToSnowMeteorologicalData] = useScroll();
    const [snowBaseDataRef, scrollToSnowBaseData] = useScroll();
    const [snowDataRef, scrollToSnowData] = useScroll();
    const [observedAvalanchesRef, scrollToObservedAvalanches] = useScroll();
    const [avalancheDangerRef, scrollToAvalancheDanger] = useScroll();

    const defaultDate = new Date();
    defaultDate.setHours(8, 0, 0, 0);

    const closeAndSaveRef = React.useRef(false);
    const confirmAndSaveRef = React.useRef(false);

    const model1Schema = useModel1Schema();

    const getPreparedData = (values, statusOverride) => {
        const {station, status, ...rest} = values;
        return {
            stationId: station.id,
            status: statusOverride ?? status,
            elevation: station.elevation,
            ...rest,
            geom: {
                type: 'Point',
                coordinates: [station.geom.coordinates[0], station.geom.coordinates[1]]
            }
        };
    };

    const formik = useFormik({
        initialValues: {
            status: initialData.status ?? "OPEN",
            station: initialData.station ?? null,
            datetime: initialData.datetime ? parseISO(initialData.datetime) : defaultDate,

            period: initialData.period ?? "H24",

            // Snow Meteorological Data
            ww: initialData.ww ?? '//',
            v: initialData.v ?? "/",
            n: initialData.n ?? "/",
            vq1: initialData.vq1 ?? "/",
            vq2: initialData.vq2 ?? "/",
            ta: initialData.ta ?? "",
            tMin: initialData.tMin ?? "",
            tMax: initialData.tMax ?? "",

            // Snow Base Data
            hs: initialData.hs ?? "",
            hn: initialData.hn ?? "",
            hnCum: initialData.hnCum ?? "",
            hnStim: initialData.hnStim ?? "",
            hnR: initialData.hnR ?? false,
            hnT: initialData.hnT ?? false,
            hnG: initialData.hnG ?? false,
            hnW: initialData.hnW ?? false,
            hnMm: initialData.hnMm ?? false,
            hnMe: initialData.hnMe ?? false,

            // Snow Data
            ps: initialData.ps ?? "",
            th10: initialData.th10 ?? "",
            th30: initialData.th30 ?? "",
            pr: initialData.pr ?? "",
            pf: initialData.pf ?? "//",
            sf: initialData.sf ?? "/",
            sh: initialData.sh ?? "/",

            // Observed Avalanches
            l1: initialData.l1 ?? "/",
            l2: initialData.l2 ?? "/",
            l3: initialData.l3 ?? "/",
            l4: initialData.l4 ?? "/",
            l5: initialData.l5 ?? "/",
            l6: initialData.l6 ?? "/",

            // Avalanche Danger
            l7: initialData.l7 ?? "/",
            l8: initialData.l8 ?? "/",
            l9: initialData.l9 ?? "/",

            // notes
            notes: initialData.notes ?? ""
        },
        validationSchema: model1Schema,
        onSubmit: async (values) => {
            const data = getPreparedData(values, confirmAndSaveRef.current ? "CONFIRMED" : (closeAndSaveRef.current ? "CLOSED" : undefined));
            await onSubmit(data);
        },
    });

    const warnings = useValidations(initialData);

    const infoSectionError = (
        (formik.touched?.station && Boolean(formik.errors?.station))
        || (formik.touched?.datetime && Boolean(formik.errors?.datetime))
    );

    const hasWarnings = (attr) => {
        const changed = formik.values[attr] !== formik.initialValues[attr];
        return !changed && Boolean(warnings[attr]);
    }

    const infoSectionWarning = ['station', 'datetime'].some(hasWarnings)
    const snowMeteorologicalDataWarning = ['ww', 'v', 'n', 'vq1', 'vq2', 'ta', 'tMin', 'tMax'].some(hasWarnings)
    const snowBaseDataWarning = ['hs', 'hn', 'hnCum', 'hnStim'].some(hasWarnings)
    const snowDataWarning = ['ps', 'th10', 'th30', 'pr', 'pf', 'sf', 'sh'].some(hasWarnings)
    const observedAvalanchesWarning = ['l1', 'l2', 'l3', 'l4', 'l5', 'l6'].some(hasWarnings)
    const avalancheDangerWarning = ['l7', 'l8', 'l9'].some(hasWarnings)

    if (model1Registry.isLoading) {
        return <LoadingOverlay />
    }

    // Disable submit when the stationForm has been submitted and it is invalid
    const submitDisabled = formik.isSubmitting || (formik.submitCount > 0 && !formik.isValid)
    const hasError = (formik.submitCount > 0 && !formik.isValid);

    const handleValidationAndSave = async (e, type) => {
        const isConfirm = type === "CONFIRM";
        const innerOnConfirm = async () => {
            if (isConfirm) {
                confirmAndSaveRef.current = true;
            } else {
                closeAndSaveRef.current = true;
            }
            await formik.handleSubmit(e);
        }

        const data = getPreparedData(formik.values);

        let failedValidations = [];
        try {
            failedValidations = await model1FailedValidationsMutation.mutateAsync(data);
        } catch (error) {
            // Se la chiamata fallisce, procedi senza mostrare errori
        }

        const validationRegistry = model1ValidationRegistryQuery.data || {};
        const errorMessages = (failedValidations || []).map(code => validationRegistry[code] || code);

        const title = isConfirm ? t('model1Form.confirmAndSaveDialog.title') : t('model1Form.closeAndSaveDialog.title');
        const defaultDescription = isConfirm ? t('model1Form.confirmAndSaveDialog.description') : t('model1Form.closeAndSaveDialog.description');
        const defaultConfirmationText = isConfirm ? t('model1Form.confirmAndSaveDialog.confirmationText') : t('model1Form.closeAndSaveDialog.confirmationText');

        askConfirmation({
            title: title,
            description: (
                <MDBox>
                    {errorMessages.length > 0 && (
                        <>
                            <MDTypography variant="body2" color="text" sx={{ fontWeight: "bold" }}>
                                {t('model1Form.failedValidationsDialog.description')}
                            </MDTypography>
                            <MDBox component="ul" sx={{ mt: 1, mb: 2, pl: 2 }}>
                                {errorMessages.map((msg, index) => (
                                    <MDTypography key={index} component="li" variant="body2" color="text">
                                        {msg}
                                    </MDTypography>
                                ))}
                            </MDBox>
                        </>
                    )}
                    <MDTypography variant="body2" color="text">
                        {defaultDescription}
                    </MDTypography>
                </MDBox>
            ),
            confirmationText: errorMessages.length > 0 ? t('model1Form.failedValidationsDialog.confirmationText') : defaultConfirmationText,
            onConfirm: innerOnConfirm
        });
    }

    const handleCloseAndSave = async (e) => {
        await handleValidationAndSave(e, "CLOSE");
    }

    const handleConfirmAndSave = async (e) => {
        await handleValidationAndSave(e, "CONFIRM");
    }

    const handleDelete = async () => {
        await deleteModel1.mutateAsync({id: initialData.id});
    }

    const showSnowMeteorologicalData = snowMeteorologicalDataColumns.some(col => visibleColumns?.[col]);
    const showSnowBaseData = snowBaseDataColumns.some(col => visibleColumns?.[col]);
    const showSnowData = snowDataColumns.some(col => visibleColumns?.[col]);
    const showObservedAvalanches = observedAvalanchesColumns.some(col => visibleColumns?.[col]);
    const showAvalancheDanger = avalancheDangerColumns.some(col => visibleColumns?.[col]);
    const showNotes = visibleColumns?.['notes'];

    return <Grid container columnSpacing={4} sx={{mb: 20}}>
        <Grid item xs={12} xl={8}>
            <Card sx={{mt: 4, px: 3, pb: 3}}>
                <MDBox variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                       mx={2} mt={-3} p={2} mb={1} textAlign="center">
                    <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                        {title}
                    </MDTypography>
                </MDBox>
                <Grid container rowSpacing={3}>
                    <Grid item xs={12} ref={infoSectionRef} sx={{scrollMarginTop: scrollMarginTop}}>
                        <InfoSection formik={formik} model1Registry={model1Registry} warnings={warnings} inputDisabled={inputDisabled}/>
                    </Grid>
                    {
                        showSnowMeteorologicalData &&
                        <Grid item xs={12} ref={snowMeteorologicalDataRef} sx={{scrollMarginTop: scrollMarginTop}}>
                            <SnowMeteorologicalData formik={formik} model1Registry={model1Registry} warnings={warnings}
                                                    inputDisabled={inputDisabled} visibleColumns={visibleColumns} />
                        </Grid>
                    }
                    {
                        showSnowBaseData &&
                        <Grid item xs={12} ref={snowBaseDataRef} sx={{scrollMarginTop: scrollMarginTop}}>
                            <SnowBaseData formik={formik} model1Registry={model1Registry} warnings={warnings}
                                          inputDisabled={inputDisabled} visibleColumns={visibleColumns}/>
                        </Grid>
                    }
                    {
                        showSnowData &&
                        <Grid item xs={12} ref={snowDataRef} sx={{scrollMarginTop: scrollMarginTop}}>
                            <SnowData formik={formik} model1Registry={model1Registry} warnings={warnings}
                                      inputDisabled={inputDisabled} visibleColumns={visibleColumns}/>
                        </Grid>
                    }
                    {
                        showObservedAvalanches &&
                        <Grid item xs={12} ref={observedAvalanchesRef} sx={{scrollMarginTop: scrollMarginTop}}>
                            <ObservedAvalanches formik={formik} model1Registry={model1Registry} warnings={warnings}
                                                inputDisabled={inputDisabled} visibleColumns={visibleColumns}/>
                        </Grid>
                    }
                    {
                        showAvalancheDanger &&
                        <Grid item xs={12} ref={avalancheDangerRef} sx={{scrollMarginTop: scrollMarginTop}}>
                            <AvalancheDanger formik={formik} model1Registry={model1Registry} warnings={warnings}
                                             inputDisabled={inputDisabled} visibleColumns={visibleColumns}/>
                        </Grid>
                    }
                    {
                        showNotes && <Grid item xs={12}>
                            <Notes formik={formik} inputDisabled={inputDisabled} visibleColumns={visibleColumns}/>
                        </Grid>
                    }
                    {
                        hasError && <Grid item xs={12} sx={{display: "flex", justifyContent: "center"}} >
                            <MDTypography variant="button" color="error" fontWeight="regular">
                                {t('model1Form.fixErrorsToProceed')}
                            </MDTypography>
                        </Grid>
                    }
                    <Grid item xs={12} sx={{display: "flex", justifyContent: "flex-end", gap: 2}}>
                        {!inputDisabled && authorization.confirm && (formik.values.status === "OPEN" || formik.values.status === "CLOSED") &&
                            <MDLoadingButton onClick={handleConfirmAndSave}
                                             loading={formik.isSubmitting}
                                             disabled={submitDisabled}
                                             variant="outlined"
                                             color="success">
                                {t('model1Form.confirmAndSave')}
                            </MDLoadingButton>
                        }
                        {
                            !inputDisabled && authorization.delete &&
                            <MDDeleteButton onDelete={handleDelete}
                                            dialogTitle={t('model1Delete.dialogTitle')}
                                            dialogMessage={t('model1Delete.dialogMessage')}/>
                        }
                        <MDBox flexGrow={1}/>
                        {!inputDisabled && authorization.edit && formik.values.status === "OPEN" &&
                            <MDLoadingButton onClick={handleCloseAndSave}
                                             loading={formik.isSubmitting}
                                             disabled={submitDisabled}
                                             variant="outlined"
                                             color="info">
                                {t('model1Form.closeAndSave')}
                            </MDLoadingButton>
                        }
                        {!inputDisabled && authorization.edit && <MDLoadingButton onClick={formik.handleSubmit}
                                                                                 name="saveButton"
                                                                                 label="save"
                                                                                 loading={formik.isSubmitting}
                                                                                 disabled={submitDisabled}
                                                                                 variant="gradient"
                                                                                 color="info">
                            {t('model1Form.save')}
                        </MDLoadingButton>
                        }
                    </Grid>
                </Grid>
            </Card>
            <AuditInfo {...initialData} />
        </Grid>

        <Grid item xs={4} sx={{position: "relative", display: {xs: 'none', xl: 'block'}}}>
            <Card sx={{mt: 4, p: 2, position: "sticky", top: scrollMarginTop}}>
                <NavButton name={t('model1Form.infoSection')} onClick={scrollToInfoSection}
                           error={infoSectionError} warning={infoSectionWarning}/>
                {
                    showSnowMeteorologicalData &&
                    <NavButton name={t('model1Form.snowMeteorologicalData')} onClick={scrollToSnowMeteorologicalData}
                               error={false} warning={snowMeteorologicalDataWarning}/>
                }
                {
                    showSnowBaseData &&
                    <NavButton name={t('model1Form.snowBaseData')} onClick={scrollToSnowBaseData}
                               error={false} warning={snowBaseDataWarning}/>
                }
                {
                    showSnowData &&
                    <NavButton name={t('model1Form.snowData')} onClick={scrollToSnowData}
                               error={false} warning={snowDataWarning}/>
                }
                {
                    showObservedAvalanches &&
                    <NavButton name={t('model1Form.observedAvalanches')} onClick={scrollToObservedAvalanches}
                               error={false} warning={observedAvalanchesWarning}/>
                }
                {
                    showAvalancheDanger &&
                    <NavButton name={t('model1Form.avalancheDanger')} onClick={scrollToAvalancheDanger}
                               error={false} warning={avalancheDangerWarning}/>
                }
            </Card>
        </Grid>
    </Grid>
}

const NavButton = ({onClick, name, error = false, warning = false}) => {
    return <MDButton onClick={onClick} variant="text" fullWidth sx={{justifyContent: "flex-start"}}
                     color={error ? "error" : (warning ? "warning" : "info")}>
        {error && <Icon color="error" fontSize="small" sx={{mr: 1}}>error</Icon>}
        {!error && warning && <Icon color="warning" fontSize="small" sx={{mr: 1}}>priority_high</Icon>}
        {name}
    </MDButton>
}
export default Model1Form;